import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, MapPin, Search, X } from "lucide-react";
import {
  countryToFlag,
  DESTINATION_REGIONS,
  FEATURED_DESTINATIONS,
  POPULAR_DESTINATIONS,
} from "../../constants/worldCountries";

function DestinationPicker({ id, name, value, onChange, required }) {
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const normalizedQuery = query.trim().toLowerCase();

  const filteredRegions = useMemo(() => {
    if (!normalizedQuery) return DESTINATION_REGIONS;

    return DESTINATION_REGIONS.map((region) => ({
      ...region,
      destinations: region.destinations.filter((dest) =>
        dest.toLowerCase().includes(normalizedQuery)
      ),
    })).filter((region) => region.destinations.length > 0);
  }, [normalizedQuery]);

  const flatResults = useMemo(() => {
    if (!normalizedQuery) return [];
    return POPULAR_DESTINATIONS.filter((dest) =>
      dest.toLowerCase().includes(normalizedQuery)
    );
  }, [normalizedQuery]);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setQuery("");
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        setQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    const timer = window.setTimeout(() => searchRef.current?.focus(), 50);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
      window.clearTimeout(timer);
    };
  }, [open]);

  const selectDestination = (destination) => {
    onChange(destination);
    setOpen(false);
    setQuery("");
  };

  const clearSelection = () => {
    onChange("");
  };

  const renderOption = (destination) => {
    const selected = value === destination;

    return (
      <button
        key={destination}
        type="button"
        role="option"
        aria-selected={selected}
        className={`packages-dest-option ${selected ? "packages-dest-option--selected" : ""}`}
        onClick={() => selectDestination(destination)}
      >
        <span className="packages-dest-option-flag" aria-hidden="true">
          {countryToFlag(destination)}
        </span>
        <span className="packages-dest-option-label">{destination}</span>
        {selected && <Check className="packages-dest-option-check" aria-hidden="true" />}
      </button>
    );
  };

  return (
    <div
      ref={rootRef}
      className={`packages-dest-picker ${open ? "packages-dest-picker--open" : ""}`}
    >
      <input type="hidden" id={id} name={name} value={value} required={required} readOnly />

      <div className="packages-dest-trigger-wrap">
        <button
          type="button"
          className="packages-dest-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby={`${id}-label`}
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className="packages-dest-trigger-icon-wrap" aria-hidden="true">
            {value ? (
              <span className="packages-dest-trigger-flag">{countryToFlag(value)}</span>
            ) : (
              <MapPin className="packages-dest-trigger-icon" />
            )}
          </span>

          <span className={`packages-dest-trigger-text ${value ? "" : "packages-dest-trigger-text--placeholder"}`}>
            {value || "Where would you like to go?"}
          </span>

          <ChevronDown className="packages-dest-chevron" aria-hidden="true" />
        </button>

        {value && (
          <button
            type="button"
            className="packages-dest-clear"
            aria-label="Clear destination"
            onClick={clearSelection}
          >
            <X className="packages-dest-clear-icon" />
          </button>
        )}
      </div>

      {open && (
        <div className="packages-dest-panel" role="listbox" aria-label="Destinations">
          <div className="packages-dest-search-wrap">
            <Search className="packages-dest-search-icon" aria-hidden="true" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="packages-dest-search"
              placeholder="Search destinations..."
              aria-label="Search destinations"
            />
            {query && (
              <button
                type="button"
                className="packages-dest-search-clear"
                aria-label="Clear search"
                onClick={() => setQuery("")}
              >
                <X className="packages-dest-search-clear-icon" />
              </button>
            )}
          </div>

          {!normalizedQuery && (
            <div className="packages-dest-featured">
              <p className="packages-dest-section-label">Popular picks</p>
              <div className="packages-dest-chips">
                {FEATURED_DESTINATIONS.map((destination) => (
                  <button
                    key={destination}
                    type="button"
                    className={`packages-dest-chip ${value === destination ? "packages-dest-chip--active" : ""}`}
                    onClick={() => selectDestination(destination)}
                  >
                    <span className="packages-dest-chip-flag" aria-hidden="true">
                      {countryToFlag(destination)}
                    </span>
                    {destination}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="packages-dest-list">
            {normalizedQuery ? (
              flatResults.length > 0 ? (
                flatResults.map(renderOption)
              ) : (
                <p className="packages-dest-empty">No destinations match &ldquo;{query}&rdquo;</p>
              )
            ) : (
              filteredRegions.map((region) => (
                <div key={region.id} className="packages-dest-group">
                  <p className="packages-dest-section-label">{region.label}</p>
                  <div className="packages-dest-group-list">
                    {region.destinations.map(renderOption)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DestinationPicker;
